import * as z from "zod";
import type { ApiContext } from "../context";
import {
  type ApiNotAuthorized,
  ErrorCode,
  InvalidFormField,
  MarketingActionRefused,
  MarketingCampaignNotFound,
  MarketingContactNotFound,
  MarketingTemplateNotFound,
  UnexpectedApiError,
  ValidationError,
} from "../errors";
import { parse } from "../model/common";
import {
  MarketingCampaign,
  MarketingCampaignDetail,
  MarketingContact,
  MarketingContactList,
  type MarketingContactQuery,
  MarketingFirebaseSync,
  MarketingImportResult,
  MarketingTemplate,
  type MarketingTemplatePatch,
  type NewMarketingTemplate,
} from "../model/marketing";
import { forward, type Result } from "../result";
import type { Transport } from "../transport";

const TemplateList = z.object({ templates: z.array(MarketingTemplate) });
const CampaignList = z.object({ campaigns: z.array(MarketingCampaign) });
const TestSend = z.object({ messageId: z.string() });
const Unsubscribed = z.object({ unsubscribed: z.boolean() });

/**
 * `/admin/marketing`: the contact list, the templates, and the campaigns built
 * from them. Staff work, like the shop importer, and behind the same guard.
 *
 * `unsubscribe` is the exception and is **public** — it is the link in an email
 * footer, authorised by the HMAC it carries rather than by a session. Call it
 * with `anonymous`.
 */
export class MarketingApi {
  constructor(private readonly t: Transport) {}

  // ---- contacts ----

  /** The contact list. The totals cover the whole list, not the page returned. */
  async contacts(
    ctx: ApiContext,
    query: MarketingContactQuery = {},
  ): Promise<Result<MarketingContactList, ApiNotAuthorized>> {
    const r = await this.t.get(ctx, "/admin/marketing/contacts", { query: { ...query } });
    return forward(
      r,
      (j) => parse(MarketingContactList, j),
      (e) => {
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  /** Adds one address by hand. An address already on the list comes back unchanged, opt-out intact. */
  async addContact(
    ctx: ApiContext,
    email: string,
    name?: string,
  ): Promise<Result<MarketingContact, ApiNotAuthorized | InvalidFormField>> {
    const r = await this.t.post(ctx, "/admin/marketing/contacts", { body: { email, name } });
    return forward(
      r,
      (j) => parse(MarketingContact, j),
      (e) => {
        if (e instanceof ValidationError) return new InvalidFormField("email", e.detailedDesc, e);
        return e;
      },
    );
  }

  /** Imports addresses from CSV. Idempotent on the lowercased address; an existing opt-out survives. */
  async importContacts(ctx: ApiContext, csv: string): Promise<Result<MarketingImportResult, ApiNotAuthorized>> {
    const r = await this.t.post(ctx, "/admin/marketing/contacts/import", { body: { csv } });
    return forward(
      r,
      (j) => parse(MarketingImportResult, j),
      (e) => {
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  /**
   * Adds every Firebase identity that has an email. Anonymous storefront sign-ins
   * have none and are skipped.
   *
   * TODO: no e2e coverage yet — `marketing.test.ts` needs a case that mints an
   * identity through `utils/firebase` and asserts it lands in `contacts()`.
   */
  async syncFirebaseContacts(ctx: ApiContext): Promise<Result<MarketingFirebaseSync, ApiNotAuthorized>> {
    const r = await this.t.post(ctx, "/admin/marketing/contacts/sync-firebase");
    return forward(
      r,
      (j) => parse(MarketingFirebaseSync, j),
      (e) => {
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  /** Opts one contact out, or back in — the only path that ever re-subscribes an address. */
  async setContactUnsubscribed(
    ctx: ApiContext,
    contactId: string,
    unsubscribed: boolean,
  ): Promise<Result<MarketingContact, ApiNotAuthorized | MarketingContactNotFound>> {
    const r = await this.t.patch(ctx, `/admin/marketing/contacts/${contactId}`, { body: { unsubscribed } });
    return forward(
      r,
      (j) => parse(MarketingContact, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.code === ErrorCode.DoesntExist) return new MarketingContactNotFound(contactId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** Removes the address and its delivery history. Prefer unsubscribing: an import can re-add a deleted address. */
  async removeContact(
    ctx: ApiContext,
    contactId: string,
  ): Promise<Result<MarketingContact, ApiNotAuthorized | MarketingContactNotFound>> {
    const r = await this.t.delete(ctx, `/admin/marketing/contacts/${contactId}`);
    return forward(
      r,
      (j) => parse(MarketingContact, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.code === ErrorCode.DoesntExist) return new MarketingContactNotFound(contactId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  // ---- templates ----

  async templates(ctx: ApiContext): Promise<Result<MarketingTemplate[], ApiNotAuthorized>> {
    const r = await this.t.get(ctx, "/admin/marketing/templates");
    return forward(
      r,
      (j) => parse(TemplateList, j).templates,
      (e) => {
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  async createTemplate(
    ctx: ApiContext,
    template: NewMarketingTemplate,
  ): Promise<Result<MarketingTemplate, ApiNotAuthorized | InvalidFormField>> {
    const r = await this.t.post(ctx, "/admin/marketing/templates", { body: template });
    return forward(
      r,
      (j) => parse(MarketingTemplate, j),
      (e) => {
        if (e instanceof ValidationError) return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        return e;
      },
    );
  }

  async template(
    ctx: ApiContext,
    templateId: string,
  ): Promise<Result<MarketingTemplate, ApiNotAuthorized | MarketingTemplateNotFound>> {
    const r = await this.t.get(ctx, `/admin/marketing/templates/${templateId}`);
    return forward(
      r,
      (j) => parse(MarketingTemplate, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.code === ErrorCode.DoesntExist) return new MarketingTemplateNotFound(templateId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** Edits a template. Campaigns already built from it keep their own copy of the body. */
  async updateTemplate(
    ctx: ApiContext,
    templateId: string,
    patch: MarketingTemplatePatch,
  ): Promise<Result<MarketingTemplate, ApiNotAuthorized | MarketingTemplateNotFound | InvalidFormField>> {
    const r = await this.t.patch(ctx, `/admin/marketing/templates/${templateId}`, { body: patch });
    return forward(
      r,
      (j) => parse(MarketingTemplate, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.code === ErrorCode.DoesntExist) return new MarketingTemplateNotFound(templateId, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** Deletes a template. The shop-claim one is transactional and is refused. */
  async removeTemplate(
    ctx: ApiContext,
    templateId: string,
  ): Promise<Result<MarketingTemplate, ApiNotAuthorized | MarketingTemplateNotFound | MarketingActionRefused>> {
    const r = await this.t.delete(ctx, `/admin/marketing/templates/${templateId}`);
    return forward(
      r,
      (j) => parse(MarketingTemplate, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.code === ErrorCode.DoesntExist) return new MarketingTemplateNotFound(templateId, e);
          return new MarketingActionRefused(e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /**
   * Sends one real email, immediately, outside the queue and the contact list.
   * Renders exactly as a delivery would, unsubscribe footer included.
   *
   * TODO: no e2e coverage yet, and it cannot simply be added — the local stack
   * points at the real SES account, so a test that calls this mails a real
   * address. It needs a sink address or a suppressed SES configuration set first.
   */
  async sendTemplateTest(
    ctx: ApiContext,
    templateId: string,
    email: string,
  ): Promise<Result<string, ApiNotAuthorized | MarketingTemplateNotFound | InvalidFormField>> {
    const r = await this.t.post(ctx, `/admin/marketing/templates/${templateId}/test`, { body: { email } });
    return forward(
      r,
      (j) => parse(TestSend, j).messageId,
      (e) => {
        if (e instanceof ValidationError) {
          if (e.code === ErrorCode.DoesntExist) return new MarketingTemplateNotFound(templateId, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  // ---- campaigns ----

  async campaigns(ctx: ApiContext): Promise<Result<MarketingCampaign[], ApiNotAuthorized>> {
    const r = await this.t.get(ctx, "/admin/marketing/campaigns");
    return forward(
      r,
      (j) => parse(CampaignList, j).campaigns,
      (e) => {
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  /**
   * Snapshots a template and freezes the recipients to every contact subscribed
   * right now. Created as DRAFT — nothing is sent until it is started.
   */
  async createCampaign(
    ctx: ApiContext,
    name: string,
    templateId: string,
  ): Promise<Result<MarketingCampaign, ApiNotAuthorized | MarketingTemplateNotFound>> {
    const r = await this.t.post(ctx, "/admin/marketing/campaigns", { body: { name, templateId } });
    return forward(
      r,
      (j) => parse(MarketingCampaign, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.code === ErrorCode.DoesntExist) return new MarketingTemplateNotFound(templateId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  async campaign(
    ctx: ApiContext,
    campaignId: string,
  ): Promise<Result<MarketingCampaignDetail, ApiNotAuthorized | MarketingCampaignNotFound>> {
    const r = await this.t.get(ctx, `/admin/marketing/campaigns/${campaignId}`);
    return forward(
      r,
      (j) => parse(MarketingCampaignDetail, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.code === ErrorCode.DoesntExist) return new MarketingCampaignNotFound(campaignId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** Moves a DRAFT or PAUSED campaign to SENDING. The background sender picks it up on its next tick. */
  async startCampaign(
    ctx: ApiContext,
    campaignId: string,
  ): Promise<Result<MarketingCampaign, ApiNotAuthorized | MarketingActionRefused>> {
    return this.transition(ctx, campaignId, "start");
  }

  /** Pauses a SENDING campaign. A message already handed to SES cannot be recalled. */
  async pauseCampaign(
    ctx: ApiContext,
    campaignId: string,
  ): Promise<Result<MarketingCampaign, ApiNotAuthorized | MarketingActionRefused>> {
    return this.transition(ctx, campaignId, "pause");
  }

  /** Final. Everything still queued is written off as skipped so the counts stay whole. */
  async cancelCampaign(
    ctx: ApiContext,
    campaignId: string,
  ): Promise<Result<MarketingCampaign, ApiNotAuthorized | MarketingActionRefused>> {
    return this.transition(ctx, campaignId, "cancel");
  }

  // ---- public ----

  /**
   * The unsubscribe link from an email footer. Public: authorised by the HMAC
   * issued with the address, not by a session, so `anonymous` is the context.
   *
   * Answers `true` for any valid signature, including an address that was never
   * on the list — it is not a membership oracle.
   */
  async unsubscribe(
    ctx: ApiContext,
    email: string,
    token: string,
  ): Promise<Result<boolean, ApiNotAuthorized | InvalidFormField>> {
    const r = await this.t.post(ctx, "/marketing/unsubscribe", { body: { email, token } });
    return forward(
      r,
      (j) => parse(Unsubscribed, j).unsubscribed,
      (e) => {
        if (e instanceof ValidationError) return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        return e;
      },
    );
  }

  /**
   * The three state changes answer identically: the refusal means either no such
   * campaign or the wrong state for this move, and the backend does not say
   * which — the next refresh shows it.
   */
  private async transition(
    ctx: ApiContext,
    campaignId: string,
    action: "start" | "pause" | "cancel",
  ): Promise<Result<MarketingCampaign, ApiNotAuthorized | MarketingActionRefused>> {
    const r = await this.t.post(ctx, `/admin/marketing/campaigns/${campaignId}/${action}`);
    return forward(
      r,
      (j) => parse(MarketingCampaign, j),
      (e) => {
        if (e instanceof ValidationError) return new MarketingActionRefused(e.detailedDesc, e);
        return e;
      },
    );
  }
}
